/* RESPONSIBLE TEAM: team-frontend-tech */
import { gt, equal, not, filterBy, readOnly, notEmpty } from '@ember/object/computed';
import Fragment from 'ember-data-model-fragments/fragment';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import Predicate from 'predicates/models/predicate';
import PredicateGroup from 'predicates/models/predicate-group';
import { findBy, isAny, isEvery, ternary, ternaryToProperty } from '@intercom/pulse/lib/computed-properties';
import { USER_ROLE_PREDICATE, LEAD_ROLE_PREDICATE } from 'predicates/models/data/roles';
import { inject as service } from '@ember/service';
import { copy } from 'ember-copy';
import { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
export default Fragment.extend({
  appService: service(),
  app: readOnly('appService.app'),
  predicates: fragmentArray('predicates/predicate', {
    polymorphic: true,
    typeKey: 'subclass',
    defaultValue: () => []
  }),
  isValid: isEvery('predicates', 'isValid', true),
  isOrMode: isAny('predicates', 'type', 'or'),
  isAndMode: not('isOrMode'),
  logicalMode: ternary('isOrMode', 'or', 'and'),
  basePredicates: ternaryToProperty('logicalPredicate', 'logicalPredicate.predicates', 'predicates'),
  hasPredicates: gt('predicates.length', 0),
  hasEditablePredicates: gt('editablePredicates.length', 0),
  isEmpty: equal('predicates.length', 0),
  rootPredicates: filterBy('predicates', 'isRoot', true),
  editablePredicates: computed('basePredicates.@each.{isLogicalType,isEditable}', function () {
    return this.basePredicates.filter(p => p.isLogicalType || p.isEditable);
  }),
  userPredicate: findBy('predicates', 'isUserPredicate', true),
  logicalPredicate: findBy('predicates', 'isLogicalType', true),
  userType: readOnly('userPredicate.userType'),
  contentEventPredicates: filterBy('predicates', 'isContentEventPredicate'),
  hasContentEventPredicates: notEmpty('contentEventPredicates'),
  attributeListOverride: null,
  hasSecondLevelPredicates() {
    return this.predicates.any(topLevelPredicate => topLevelPredicate.predicates && topLevelPredicate.predicates.any(secondLevelPredicate => secondLevelPredicate.predicates));
  },
  add(predicateProperties, {
    parentPredicate,
    context
  } = {}) {
    if (parentPredicate && !(parentPredicate instanceof Predicate)) {
      throw new Error('parentPredicate must be an instance of Predicate');
    }
    if (predicateProperties instanceof Predicate) {
      throw new Error('predicateProperties cannot be an instance of Predicate');
    }
    let newPredicate = Predicate.createPredicateWithDefaults(this.store, predicateProperties, context);
    if (newPredicate.isRoot) {
      if (parentPredicate) {
        throw new Error(`${newPredicate.type} predicates are root predicates, so they cannot have a parent predicate`);
      }
      this.predicates.pushObject(newPredicate);
    } else if (parentPredicate) {
      parentPredicate = this._wrapIfRequired(parentPredicate);
      parentPredicate.predicates.pushObject(newPredicate);
      this._ensureTopLevelLogicalWrapper();
    } else {
      this.basePredicates.pushObject(newPredicate);
    }
    return newPredicate;
  },
  remove(predicate) {
    if (predicate && !(predicate instanceof Predicate)) {
      throw new Error('predicate must be an instance of Predicate');
    }
    let parentPredicate = this._findParentPredicate(predicate);
    if (parentPredicate) {
      parentPredicate = this._wrapIfRequired(parentPredicate);
      parentPredicate.predicates.removeObject(predicate);
      this._unwrapIfRequired(parentPredicate);
      this._removeUnnecessaryTopLevelLogicalWrapper();
      this._removeEmptyLogicalPredicate(parentPredicate);
    } else {
      this.predicates.removeObject(predicate);
      this._removeEmptyLogicalPredicate(this.logicalPredicate);
    }
  },
  removeAll(removeCondition) {
    let predicate;
    let predicatesRemoved = [];
    while ((predicate = this._findPredicate(removeCondition)) !== null) {
      this.remove(predicate);
      predicatesRemoved.push(predicate.attribute);
    }
    return predicatesRemoved;
  },
  _findPredicate(findCondition) {
    for (let predicate of Predicate.walkPredicates(this.predicates)) {
      if (findCondition(predicate)) {
        return predicate;
      }
    }
    return null;
  },
  _findParentPredicate(childPredicate) {
    for (let parentPredicate of Predicate.walkPredicates(this.predicates)) {
      if (parentPredicate.predicates && parentPredicate.predicates.indexOf(childPredicate) !== -1) {
        return parentPredicate;
      }
    }
  },
  _findCollectionContainingPredicate(childPredicate) {
    let parentPredicate = this._findParentPredicate(childPredicate);
    return parentPredicate ? parentPredicate.predicates : this.predicates;
  },
  _replacePredicate(oldPredicate, newPredicate) {
    let parentPredicates = this._findCollectionContainingPredicate(oldPredicate);
    let index = parentPredicates.indexOf(oldPredicate);
    parentPredicates.replace(index, 1, [newPredicate]);
  },
  _wrapIfRequired(predicate) {
    if (predicate.isLogicalType) {
      return predicate;
    }
    let andPredicate = Predicate.createPredicateWithGenericType(this.store, {
      type: 'and',
      predicates: [predicate.serialize()]
    });
    this._replacePredicate(predicate, andPredicate);
    return andPredicate;
  },
  _unwrapIfRequired(predicate) {
    if (!predicate.isLogicalType || predicate.predicates.length !== 1 || !this._findParentPredicate(predicate)) {
      return;
    }
    let remainingPredicate = predicate.predicates.firstObject;
    this._replacePredicate(predicate, copy(remainingPredicate));
  },
  _ensureTopLevelLogicalWrapper() {
    if (this.hasSecondLevelPredicates()) {
      return;
    }
    let nonRootPredicates = this.predicates.filter(p => !p.isRoot);
    this.predicates.removeObjects(nonRootPredicates);
    this.predicates.pushObject(Predicate.createPredicateWithGenericType(this.store, {
      type: 'and',
      predicates: nonRootPredicates.map(p => p.serialize())
    }));
  },
  // For cases where we have mixed logical and attribute predicate at the root,
  // or we have multiple logical predicates at the root,
  // we need to wrap these in an explicit AND predicate.
  initializeTopLevelLogicalWrapper() {
    let topLevelEditablePredicates = this.predicates.filter(p => p.isEditable);
    let topLevelLogicalPredicates = this.predicates.filter(p => p.isLogicalType);
    if (topLevelEditablePredicates.length && topLevelLogicalPredicates.length || topLevelLogicalPredicates.length > 1) {
      this._ensureTopLevelLogicalWrapper();
    }
  },
  // When setting up the predicate editor we want to remove any empty logical predicates on init
  // to ensure we onlyl render filter groups render with rules in them.
  // An empty logical predicate is one like this: { type: 'or', predicates: [] }
  removeEmptyLogicalPredicates() {
    let predicatesToRemove = this.predicates.filter(p => p.isLogicalType && isEmpty(p.predicates));
    predicatesToRemove.forEach(p => this.remove(p));
  },
  _removeEmptyLogicalPredicate(predicate) {
    let currentPredicate = predicate;
    while (currentPredicate && currentPredicate.isLogicalType && isEmpty(currentPredicate.predicates)) {
      let parentPredicate = this._findParentPredicate(currentPredicate);
      let predicateCollection = this._findCollectionContainingPredicate(currentPredicate);
      predicateCollection.removeObject(currentPredicate);
      this._removeUnnecessaryTopLevelLogicalWrapper();
      currentPredicate = parentPredicate;
    }
  },
  _removeUnnecessaryTopLevelLogicalWrapper() {
    let topLevelOrPredicate = this.predicates.find(p => p.type === 'or');
    if (topLevelOrPredicate && topLevelOrPredicate.predicates.length === 1) {
      let predicates = topLevelOrPredicate.predicates;
      this.predicates.removeObject(topLevelOrPredicate);
      this.predicates.pushObjects(predicates.map(p => Predicate.createPredicateWithGenericType(this.store, p.serialize())));
    }
    let topLevelAndPredicate = this.predicates.find(p => p.type === 'and');
    if (topLevelAndPredicate && topLevelAndPredicate.predicates.every(p => !p.isLogicalType)) {
      let topLevelPredicates = topLevelAndPredicate.predicates;
      this.predicates.removeObject(topLevelAndPredicate);
      this.predicates.pushObjects(topLevelPredicates.map(p => Predicate.createPredicateWithGenericType(this.store, p.serialize())));
    }
  },
  switchUserType(mode) {
    this.userPredicate.changeUserType(mode);
  },
  switchLogicalMode(mode) {
    let switchedPredicates;
    if (this.hasSecondLevelPredicates()) {
      this.set('logicalPredicate.type', mode);
      this.set('predicates', this.predicates);
      return;
    }
    if (mode === 'or') {
      switchedPredicates = this._generateOrLogicalMode();
    } else if (mode === 'and') {
      switchedPredicates = this._generateAndLogicalMode();
    }
    this.set('predicates', switchedPredicates);
  },
  _generateOrLogicalMode() {
    let predicates = this.rootPredicates.map(p => p.serialize());
    this.basePredicates.removeObjects(this.rootPredicates);
    return PredicateGroup.convertRawPredicates(this.store, predicates.concat([{
      type: 'or',
      predicates: this.basePredicates.serialize()
    }]));
  },
  _generateAndLogicalMode() {
    let predicates = this.rootPredicates.map(p => p.serialize());
    this.basePredicates.removeObjects(this.rootPredicates);
    return PredicateGroup.convertRawPredicates(this.store, predicates.concat(this.basePredicates.serialize()));
  }
}).reopenClass({
  convertRawPredicates(store, predicates) {
    return predicates.map(predicate => Predicate.createPredicateWithGenericType(store, predicate));
  },
  createPredicatesArray(store, predicates, attributeListOverride) {
    let predicateFragments = PredicateGroup.convertRawPredicates(store, predicates);
    //TODO Pat - A bit of a hack using the predicate-group fragment to create the array. This is fine until I work out how to create a fragmentArray directly using the Model Fragment API
    let predicateGroup = store.createFragment('predicates/predicate-group', {
      predicates: predicateFragments,
      attributeListOverride
    });
    return predicateGroup.get('predicates');
  },
  migrateAnonymousToRolePredicate(predicates) {
    return predicates.map(predicate => {
      if (predicate.type === 'anonymous') {
        return predicate.comparison === 'true' ? LEAD_ROLE_PREDICATE : USER_ROLE_PREDICATE;
      }
      return predicate;
    });
  }
});